import {
  ChatBotMessageTypes,
  OnboardingFlowStepStatus,
  OnboardingFlowStepTypes,
  OnboardingFlowType,
  SendMessageToBotResponse,
  Tool,
  useGetAllToolsQuery,
  useSendMessageToBotMutation,
  UserTask,
  UserTaskSource,
} from '__generated__/api-types-and-hooks'
import React, { useEffect, useState, useRef, useCallback, Dispatch, SetStateAction } from 'react'
import {
  AppDynamicChatMessage,
  FormComponent,
  TextComponent,
  TypingMessage,
  UIComponentProps,
} from 'components/AppOnboarding/AppChatbot/AppDynamicChatMessage/AppDynamicChatMessage.component'
import { useParams } from 'react-router-dom'
import { AppTypingIndicator } from 'components/AppOnboarding/AppChatbot/AppTypingIndicator/AppTypingIndicator.component'
import {
  audioMaxDuration,
  errorMessages,
} from 'components/AppOnboarding/AppChatbot/AppChatBotFlows/constants'
import { useInfiniteGetMessagesFromBotQuery } from '__generated__/infinite-api-types-and-hooks'
import { LoadingIndicator } from 'stream-chat-react'
import CircleNotchIcon from 'components/Common/SvgIcons/CircleNotchIcon'
import { useFlashParams } from '../../../../hooks/useFlashParams'
import { getTenantId } from 'utils/getTenantId'
import { AppPaperClipIcon } from 'components/Common/AppSvgIcons/AppPaperClipIcon'
import { AppButton } from 'components/Common/AppButton/AppButton.component'
import { AppSendMessageIcon } from 'components/Common/AppSvgIcons/AppSendMessageIcon'
import { AppPlayIcon } from 'components/Common/AppSvgIcons/AppPlayIcon'
import Avatar from 'appAssets/appImages/Avatar.png'
import { AppModalBox } from 'components/Common/AppModalBox/AppModalBox.component'
import { AppFileUploadField } from 'components/Common/AppFileUploadField/AppFileUploadField.component'
import moment from 'moment-timezone'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { getRoleFromPath, helpDesk } from 'utils/helper'
import { findLast, findLastIndex, find } from 'lodash'
import redirectTo from 'utils/redirectTo'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import { useForm } from 'react-hook-form'
import { EditIcon } from 'components/Common/SvgIcons'
import { AppAudioRecorder } from 'components/Common/AppAudioRecorder/AppAudioRecorder.component'
import {
  AppTextToSpeech,
  useTextToSpeech,
} from 'components/Common/AppTextToSpeech/AppTextToSpeech.component'
import { AppVoiceIndicator } from 'components/Common/AppVoiceIndicator/AppVoiceIndicator.component'
import {
  extractTextForTTS,
  isAudioRecordingSupported,
  isSpeechSynthesisSupported,
} from 'utils/audioUtils'
import { AppTask } from 'components/AppKanbanBoard/AppTask/AppTask.component'
import { AppTaskCreationConfirmation } from 'components/AppKanbanBoard/AppTaskCreationConfirmation/AppTaskCreationConfirmation.component'
import AppCrossIcon from 'components/Common/AppSvgIcons/AppModalCrossIcon'
import { useGetBusinessProfile } from 'appHooks/useGetBusinessProfile'
import { AppToolTip } from 'components/Common/AppToolTip/AppToolTip.component'
import { SafeMarkdown } from 'components/Common/SafeMarkdown/SafeMarkdown.component'

interface OnboardingStepMessageResponse {
  stepType?: OnboardingFlowStepTypes
  stepStatus?: OnboardingFlowStepStatus
  content?: UIComponentProps
  progress?: number
}

export const AppGetToKnowYouRoom = ({
  initialMessage,
  sessionId,
  uploadFileAction,
  flowType,
  uploadedFileId,
  uploadFileLoading,
  logo,
  userInitial,
  refetchBusinessGoal,
  setProgress,
  progress,
  selectedGoalTemplate,
  onGoalTemplateUsed,
}: {
  initialMessage?: string
  sessionId?: string
  uploadedFileId: string[]
  uploadFileLoading: boolean
  flowType?: OnboardingFlowType
  logo?: string
  userInitial?: string
  refetchBusinessGoal: () => void
  setProgress: Dispatch<SetStateAction<number>>
  progress: number
  uploadFileAction: (options: { files: FileList; isMultiple: boolean; saveFile: boolean }) => void
  selectedGoalTemplate?: string
  onGoalTemplateUsed?: () => void
}) => {
  const [messages, setMessages] = useState<
    {
      sender: string
      text?: string
      content?: UIComponentProps
      stepType?: OnboardingFlowStepTypes
      stepStatus?: OnboardingFlowStepStatus
      createdAt?: string
      isEdited?: boolean | null
      isVoiceMessage?: boolean
      voiceDuration?: number
    }[]
  >([])
  const [queuedMessages, setQueuedMessages] = useState<OnboardingStepMessageResponse[]>([])
  // const [emojiClicked, setEmojiClicked] = useState(false)

  const [inputValue, setInputValue] = useState(initialMessage)
  const [isEditingLastMessage, setIsEditingLastMessage] = useState(false)
  const [editingMessageText, setEditingMessageText] = useState('')
  const [editingStepType, setEditingStepType] = useState<OnboardingFlowStepTypes | null>(null)
  const [editingStepContent, setEditingStepContent] = useState<UIComponentProps | null>(null)
  const [currentStep, setCurrentStep] = useState<{
    stepType?: OnboardingFlowStepTypes
    stepStatus?: OnboardingFlowStepStatus
  }>({})
  const [processing, setProcessing] = useState(false)
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const [disableButton, setDisableButton] = useState(false)
  const [isUserScrolling, setIsUserScrolling] = useState(false)
  const [isUpload, setIsUpload] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<File[]>([])
  const [startUpload, setStartUpload] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)

  // Audio-related state
  const [audioSupported, setAudioSupported] = useState(false)
  const [ttsSupported, setTtsSupported] = useState(false)
  const [showCreatePlayModal, setShowCreatePlayModal] = useState(false)
  const [showPlaySuccessModal, setShowPlaySuccessModal] = useState(false)
  const [showPlayErrorModal, setShowPlayErrorModal] = useState(false)
  const [createdUserTask, setCreatedUserTask] = useState<UserTask | null>(null)
  const [isPlayCreated, setIsPlayCreated] = useState(false)
  const { data: toolsData, refetch } = useGetAllToolsQuery(
    {
      tenantId: getTenantId(),
    },
    {
      refetchOnWindowFocus: false,
    }
  )
  const tools = (toolsData?.getAllTools?.data as Tool[]) || []
  const user = useSelector((rootState: RootState) => rootState.user.user)
  const { clientId } = useParams()

  // Get assignee options and advisor data for play creation
  const { assigneeOptions, advisorsData } = useGetBusinessProfile({ enabled: true })
  // Fetch the messages with pagination support
  const {
    data: messageHistoryData,
    fetchNextPage,
    hasNextPage,
    isLoading: isMessagesLoading,
    isFetchingNextPage,
    isFetching,
    refetch: refetchMessages,
  } = useInfiniteGetMessagesFromBotQuery(
    {
      sessionId: sessionId ?? '',
    },
    {
      getNextPageParam: (lastPage) => {
        return lastPage.getMessagesFromBot.nextCursor
          ? { cursor: lastPage.getMessagesFromBot.nextCursor }
          : undefined
      },
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
    }
  )
  const { removeFlashParams } = useFlashParams<{
    connectorName: string
    toolId: string
  }>()

  // Add useForm hook
  const { handleSubmit, setValue, resetField } = useForm()

  // Text-to-speech hook
  const { speak: speakText } = useTextToSpeech()

  const scrollToBottom = useCallback(
    (force?: boolean) => {
      if (!chatContainerRef.current) return
      if (!isUserScrolling || force) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
      }
    },
    [isUserScrolling]
  )

  // Check audio support on mount
  useEffect(() => {
    setAudioSupported(isAudioRecordingSupported())
    setTtsSupported(isSpeechSynthesisSupported())
  }, [])

  // Handle selected goal template from modal
  useEffect(() => {
    if (selectedGoalTemplate) {
      setInputValue(selectedGoalTemplate)
      setIsEditingLastMessage(false)
      setDisableButton(false)
      scrollToBottom(true)
      // Clear the selected template after using it
      onGoalTemplateUsed?.()
    }
  }, [selectedGoalTemplate, onGoalTemplateUsed, scrollToBottom])

  const convertMessage = (message: {
    __typename?: 'ChatBotMessage'
    role: string
    content: string
    createdAt: string
    isEdited?: boolean | null
  }) => {
    if (message?.content?.startsWith('{')) {
      const content = JSON.parse(message.content)
      return {
        sender: message.role ?? '',
        content,
        createdAt: message.createdAt ?? '',
        isEdited: message.isEdited || false,
      }
    }
    return {
      sender: message.role ?? '',
      text: message.content ?? '',
      createdAt: message.createdAt ?? '',
      isEdited: message.isEdited || false,
    }
  }

  const isExistingMessage = (message) => {
    const isExisting = messages.find(
      (msg) =>
        msg.createdAt === message.createdAt &&
        (msg.text === message.text ||
          JSON.stringify(msg.content) === JSON.stringify(message.content))
    )
    return !isExisting
  }
  const prevSessionIdRef = useRef<string | undefined>()
  useEffect(() => {
    if (prevSessionIdRef.current !== sessionId) {
      // If the sessionId has changed, reset messages and refetch
      setMessages([]) // Clear previous messages
      prevSessionIdRef.current = sessionId
    }
  }, [sessionId])

  const addMessagesFromLatestPage = useCallback(
    (
      pageMessages: {
        __typename?: 'ChatBotMessage'
        role: string
        content: string
        createdAt: string
        isEdited?: boolean | null
      }[]
    ) => {
      const newMessages = pageMessages
        .map((message) => convertMessage(message))
        .reverse()
        .filter(Boolean)
      // Avoid adding duplicate messages by checking their createdAt and content
      setMessages((prevMessagesArray) => {
        const existingMessages = new Set(
          prevMessagesArray.map(
            (msg) => `${msg.createdAt}-${msg.text ?? JSON.stringify(msg.content)}`
          )
        )
        const oldMessages =
          newMessages?.filter((msg) => {
            const key = `${msg?.createdAt}-${msg?.text ?? JSON.stringify(msg?.content)}`
            return !existingMessages.has(key)
          }) ?? []
        return [...oldMessages, ...prevMessagesArray]
      })
    },
    []
  )

  useEffect(() => {
    if (!messageHistoryData || isRetrying) return

    const pageMessages = messageHistoryData.pages ?? []
    const lastPageMessages =
      pageMessages[pageMessages.length - 1]?.getMessagesFromBot.messages ?? []

    if (!messages.length && pageMessages.length > 1) {
      pageMessages.forEach((page) => {
        addMessagesFromLatestPage(page.getMessagesFromBot.messages ?? [])
      })
      return
    }
    if (lastPageMessages.length) {
      addMessagesFromLatestPage(lastPageMessages)
      if (progress === 0) {
        setProgress(lastPageMessages[0].progress ?? 0)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageHistoryData, sessionId, addMessagesFromLatestPage, messages.length])
  const {
    mutate: sendMessageToBot,
    data: latestResponse,
    isLoading: isSendingMessageTobot,
  } = useSendMessageToBotMutation({
    onError: async (error) => {
      if (error?.message === 'Execution timed out.') {
        const newMessage = 'There was a delay in processing. Please wait a moment...'
        addMessage({ sender: 'assistant', text: newMessage })
        setProcessing(true)
        scrollToBottom(true)
        setIsRetrying(true)

        // Try to fetch new messages for up to 30 seconds
        const intervalId = setInterval(async () => {
          const result = await refetchMessages({
            refetchPage: (_, index) => index === 0,
          })

          if (!result.data) return

          const lastMessage = result.data.pages[0]?.getMessagesFromBot?.messages?.[0]
          const lastCurrentMessage = messages[messages.length - 1]
          // If we found a new message from the bot
          if (
            lastMessage?.role === 'assistant' &&
            lastMessage?.createdAt !== lastCurrentMessage?.createdAt
          ) {
            setProcessing(false)
            const lastPageMessages = result.data.pages[0]?.getMessagesFromBot?.messages ?? []
            const differentMessages = lastPageMessages.filter(
              (message) =>
                !messages.find((m) => m.createdAt === message.createdAt) &&
                message.role === 'assistant'
            )
            // Add the new messages using the existing function
            const convertedMessages = differentMessages
              .map((message) => convertMessage(message))
              .filter((message) => isExistingMessage(message))
              .reverse()
            setMessages((prevMessages) => [...prevMessages, ...convertedMessages])
            const lastMessage = convertedMessages[0]
            if (lastMessage?.content?.type !== OnboardingFlowStepTypes.Message) {
              clearInterval(intervalId)
              setIsRetrying(false)
            }
            scrollToBottom(true)
          }
        }, 5000)
      }
    },
  })

  // Handle audio recording completion
  const handleAudioRecording = useCallback(
    async (audioBlob: Blob, transcript?: string, duration?: number) => {
      if (!audioBlob || processing || disableButton) return

      // Check if we have a transcript
      if (!transcript || transcript.trim() === '') {
        // Show user-friendly message and allow manual input
        const userInput = prompt(errorMessages.speechRecognitionFailed)
        if (!userInput || userInput.trim() === '') {
          return
        }
        transcript = userInput.trim()
      }

      try {
        // Determine if this should be treated as form input
        const lastMessageContent = messages[messages.length - 1]?.content
        const isFormContext = lastMessageContent?.type === OnboardingFlowStepTypes.Form

        const messageType = isFormContext
          ? ChatBotMessageTypes.AudioToForm
          : ChatBotMessageTypes.Audio

        // Add user message to UI immediately with voice metadata
        const userMessage = {
          sender: 'user',
          text: transcript.trim(),
          createdAt: new Date().toISOString(),
          isVoiceMessage: true,
          voiceDuration: duration || 0,
        }
        setMessages((prev) => [...prev, userMessage])
        setProcessing(true)

        // Send message using existing mutation
        sendMessageToBot(
          {
            input: {
              userMessage: transcript.trim(),
              userMessageType: messageType,
              audioTranscript: transcript.trim(),
              sessionId,
              isVoiceMessage: true,
              voiceDuration: duration || 0,
            },
          },
          {
            onError: (error) => {
              if (error.statusCode === 401) {
                window.location.reload()
              }
            },
          }
        )
      } catch (error) {
        console.error('Error processing audio:', error)
        setProcessing(false)
      }
    },
    [messages, processing, disableButton, sessionId, sendMessageToBot]
  )

  const handleNewMessage = (message: OnboardingStepMessageResponse) => {
    setCurrentStep(message)
    if ((message?.content as TextComponent)?.text?.toLowerCase().includes('goal center')) {
      refetchBusinessGoal()
    }
    const newMessage = { sender: 'assistant', content: message.content }
    setMessages((prevMessages) => [...prevMessages, newMessage])
    setIsUserScrolling(false)
    setProcessing(false)

    // Auto-play TTS for bot messages if supported
    if (ttsSupported && message.content) {
      // Use transcript from response if available (for forms), otherwise extract text from content
      const transcript = (latestResponse?.sendMessageToBot as SendMessageToBotResponse).transcript
      const textToSpeak = transcript || extractTextForTTS(message.content)

      if (textToSpeak) {
        speakText(textToSpeak, {
          onEnd: () => {},
          onError: () => {},
        })
      }
    }
  }

  useEffect(() => {
    if (latestResponse?.sendMessageToBot.responses?.length && !isRetrying) {
      const firstResponse = JSON.parse(
        latestResponse?.sendMessageToBot.responses[0]
      ) as OnboardingStepMessageResponse
      handleNewMessage(firstResponse)
      setQueuedMessages((prevMessages) => {
        const newMessages = latestResponse?.sendMessageToBot
          .responses!.slice(1)
          .map((response) => JSON.parse(response) as OnboardingStepMessageResponse)
        return [...prevMessages, ...newMessages]
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestResponse?.sendMessageToBot.responses])

  useEffect(() => {
    if (latestResponse?.sendMessageToBot.progress) {
      setProgress(latestResponse?.sendMessageToBot.progress)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestResponse?.sendMessageToBot.progress])

  useEffect(() => {
    const lastMessage = messages[messages.length - 1]
    const lastAssistantMessage = findLast(messages, (message) => message.sender === 'assistant')
    if (lastMessage?.sender === 'user') {
      scrollToBottom(true)
    }
    if (lastAssistantMessage?.content?.type === OnboardingFlowStepTypes.End) {
      const tenantId = getTenantId()
      const role = getRoleFromPath()
      const isAdvisor = user?.roles.includes('BSO_ADVISOR')
      if (isAdvisor) {
        redirectTo(`/${role}/${tenantId}/clients/${clientId ? clientId : user.id}/goals`)
      } else {
        redirectTo(`/owner-v2/${tenantId}/goals`)
      }
    }
    if (lastAssistantMessage?.content?.type === OnboardingFlowStepTypes.ToolConnection) {
      const content = lastAssistantMessage.content as { tools: Tool[] }
      const toolConnections = content.tools
      const connectedTools = toolConnections.filter((tool) =>
        tools.find((t) => t.id === tool.id && t.isConnected)
      )
      if (toolConnections.length === connectedTools.length && !isSendingMessageTobot) {
        setProcessing(true)
        sendMessageToBot(
          {
            input: {
              userMessage: JSON.stringify({ toolIds: connectedTools.map((tool) => tool.id) }),
              userMessageType: ChatBotMessageTypes.ToolConnected,
              sessionId,
            },
          },
          {
            onError: (error) => {
              if (error.statusCode === 401) {
                window.location.reload()
              }
            },
          }
        )
        removeFlashParams(['toolId'])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, tools])

  const submitDataConfirmationForm = async (formData) => {
    const filesFields = ['businessPlanFile']
    const filesToUpload = filesFields.filter((field) => formData[field])

    if (filesToUpload.length > 0) {
      filesToUpload.forEach((field) => {
        // Check if formData[field] is an array
        const files = Array.isArray(formData[field]) ? formData[field] : [formData[field]]
        uploadFileAction({
          files: files as unknown as FileList,
          isMultiple: files.length > 1, // Set isMultiple based on file count
          saveFile: true,
        })
      })
      return
    }
    setProcessing(true)
    const { id, ...data } = formData
    sendMessageToBot(
      {
        input: {
          userMessage: JSON.stringify(data),
          userMessageType: ChatBotMessageTypes.FormResponse,
          ...(formData.skip ? { skip: true } : {}),
          sessionId,
        },
      },
      {
        onError: (error) => {
          if (error.statusCode === 401) {
            window.location.reload()
          }
        },
      }
    )
  }

  const handleSendMessage = useCallback(async () => {
    const pageMessages = messageHistoryData?.pages ?? []
    const page = pageMessages.length
      ? pageMessages[pageMessages.length - 1]
      : { getMessagesFromBot: { messages: [] } }
    const lastPageMessages = page.getMessagesFromBot.messages ?? []
    if (!inputValue && lastPageMessages.length) return
    setProcessing(true)
    const userMessage = { sender: 'user', text: inputValue }
    addMessage(userMessage)
    setInputValue('')
    sendMessageToBot(
      {
        input: {
          userMessage: inputValue ?? '',
          sessionId,
          userMessageType: ChatBotMessageTypes.Text,
        },
      },
      {
        onError: (error) => {
          if (error.statusCode === 401) {
            window.location.reload()
          }
        },
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, messages])

  useEffect(() => {
    if (isMessagesLoading || isFetchingNextPage || isFetching) {
      return
    }
    const pageMessages = messageHistoryData?.pages ?? []
    const lastPageMessages =
      pageMessages[pageMessages.length - 1]?.getMessagesFromBot.messages ?? []
    if (messages.length || lastPageMessages.length) {
      return
    }
    if (!messages.length && !lastPageMessages.length) {
      handleSendMessage()
    }
  }, [
    handleSendMessage,
    isFetching,
    isFetchingNextPage,
    isMessagesLoading,
    messageHistoryData?.pages,
    messages.length,
  ])

  useEffect(() => {
    if (!uploadFileLoading && uploadedFileId && uploadedFileId.length > 0) {
      submitDataConfirmationForm({ businessPlan: uploadedFileId })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFileLoading, uploadedFileId])

  useEffect(() => {
    if (uploadedFile && uploadedFile.length > 0 && startUpload) {
      setValue('businessPlanFile', uploadedFile.length === 1 ? uploadedFile[0] : uploadedFile)
      handleSubmit((formData) => submitDataConfirmationForm({ ...formData, skip: 'true' }))()
      setUploadedFile([])
      setStartUpload(false)
      resetField('businessPlanFile')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startUpload])

  const scrollTopMessage = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollTop + 250
    }
  }

  const handleUserScroll = () => {
    if (chatContainerRef.current) {
      const isAtBottom =
        chatContainerRef.current.scrollHeight - chatContainerRef.current.scrollTop ===
        chatContainerRef.current.clientHeight
      setIsUserScrolling(!isAtBottom)

      // Trigger loading more messages when the user scrolls to the top
      if (chatContainerRef.current.scrollTop === 0 && hasNextPage) {
        fetchNextPage()
      }
    }
  }

  const handleTypingMessage = (text: string, displayText: string) => {
    if (text === displayText) {
      setDisableButton(false)
      if (queuedMessages.length && text) {
        const firstQueueMessage = queuedMessages.shift()
        handleNewMessage(firstQueueMessage!)
        setQueuedMessages(queuedMessages)
        return
      }
    } else {
      if (text?.length) {
        setDisableButton(true)
      }
    }
    if (displayText.length) {
      scrollToBottom()
    } else {
      scrollTopMessage()
    }
  }

  const addMessage = (newMessage) => {
    setMessages((prevMessages) => [...prevMessages, newMessage])
    setIsUserScrolling(false)
  }

  const handleEditLastMessage = () => {
    const lastUserMessage = findLast(messages, (msg) => msg.sender === 'user')
    if (lastUserMessage) {
      // Find the assistant message that prompted the last user message
      const lastUserMessageIndex = findLastIndex(
        messages,
        (msg) => msg.sender === 'user' && !msg.isEdited
      )
      // Look for the assistant message immediately before the last user message
      const promptingAssistantMessage = messages[lastUserMessageIndex - 1]
      setIsEditingLastMessage(true)
      setEditingMessageText(lastUserMessage.text || '')

      // Get the step type from the assistant message that prompted the user's response
      if (promptingAssistantMessage && promptingAssistantMessage.content?.type) {
        const stepType = promptingAssistantMessage.content.type as OnboardingFlowStepTypes

        if (
          stepType === OnboardingFlowStepTypes.Form ||
          stepType === OnboardingFlowStepTypes.GoalTemplate
        ) {
          // For form steps, we need to re-show the form
          setEditingStepType(OnboardingFlowStepTypes.Form)
          setEditingStepContent(
            (promptingAssistantMessage.content as FormComponent)
              ? ({
                  ...promptingAssistantMessage.content,
                  status: OnboardingFlowStepStatus.InProgress,
                } as FormComponent)
              : null
          )
        } else {
          // For question/text steps, show text input
          setEditingStepType(OnboardingFlowStepTypes.Question)
          setEditingStepContent(null)
          setInputValue(lastUserMessage.text || '')
        }
      } else {
        // Default to text input if we can't determine the step type
        setEditingStepType(OnboardingFlowStepTypes.Question)
        setEditingStepContent(null)
        setInputValue(lastUserMessage.text || '')
      }
    }
  }

  const handleCancelEdit = () => {
    setIsEditingLastMessage(false)
    setEditingMessageText('')
    setEditingStepType(null)
    setEditingStepContent(null)
    setInputValue('')
  }

  const handleSaveEdit = () => {
    const trimmedText = editingMessageText.trim()
    if (trimmedText) {
      addMessage({ sender: 'user', text: trimmedText, isEdited: true })

      // Set processing state to show loading
      setProcessing(true)

      // Send edited message with the edit flag
      sendMessageToBot(
        {
          input: {
            userMessage: trimmedText,
            sessionId: sessionId ?? '',
            userMessageType: ChatBotMessageTypes.Text,
            isEditingLastMessage: true,
          },
        },
        {
          onSuccess: (response) => {
            // Reset edit state
            setInputValue('')
            setIsEditingLastMessage(false)
            setEditingMessageText('')
            setEditingStepType(null)
            setEditingStepContent(null)
            setIsRetrying(false)
            // Note: Don't set processing to false here, let the normal response handler do it
          },
          onError: (error) => {
            setProcessing(false)
            console.error('Edit submission failed:', error)
          },
        }
      )
    } else {
      handleCancelEdit()
    }
  }

  const handleEditFormSubmit = (formData: Record<string, string>) => {
    // Update the local state immediately for the user message
    const formattedResponse = Object.values(formData).join(', ')
    setMessages((prevMessages) => {
      const lastUserMessageIndex = findLastIndex(prevMessages, (msg) => msg.sender === 'user')
      if (lastUserMessageIndex !== -1) {
        const updatedMessages = [...prevMessages]
        updatedMessages[lastUserMessageIndex] = {
          ...updatedMessages[lastUserMessageIndex],
          text: formattedResponse,
          isEdited: true,
        }
        return updatedMessages
      }
      return prevMessages
    })

    // Set processing state to show loading
    setProcessing(true)

    sendMessageToBot(
      {
        input: {
          userMessage: JSON.stringify(formData),
          sessionId: sessionId ?? '',
          userMessageType: ChatBotMessageTypes.FormResponse,
          isEditingLastMessage: true,
        },
      },
      {
        onSuccess: (response) => {
          // Reset edit state
          setIsEditingLastMessage(false)
          setEditingMessageText('')
          setEditingStepType(null)
          setEditingStepContent(null)
          setIsRetrying(false)
          // Note: Don't set processing to false here, let the normal response handler do it
        },
        onError: (error) => {
          setProcessing(false)
          console.error('Edit form submission failed:', error)
        },
      }
    )
    scrollToBottom()
  }

  // Note: Chat context generation function removed - play descriptions are now empty by default

  // Wrapper function to handle the UserTask type difference
  const handleSetCreatedUserTask: React.Dispatch<React.SetStateAction<UserTask>> = (value) => {
    if (typeof value === 'function') {
      setCreatedUserTask((prev) => value(prev!))
    } else {
      setCreatedUserTask(value)
      // If a UserTask is created successfully, disable the play button
      if (value) {
        setIsPlayCreated(true)
      }
    }
  }

  const lastMessageContent = messages[messages.length - 1]?.content
  const isFormStep =
    currentStep.stepType === OnboardingFlowStepTypes.Form ||
    (lastMessageContent?.type && lastMessageContent.type === OnboardingFlowStepTypes.Form)
  const isToolConnectionStep =
    currentStep.stepType === OnboardingFlowStepTypes.ToolConnection ||
    (lastMessageContent?.type && lastMessageContent.type === OnboardingFlowStepTypes.ToolConnection)
  return (
    <div className="flex flex-col gap-4 w-full rounded-lg h-5/6 bg-app-grey-5">
      <div className="p-2 sm:p-4 pb-0 bg-app-grey-5">
        <div className="flex justify-between items-center w-full">
          <div className="flex-1" />
          <div className="w-full bg-app-grey-5 font-bold font-inter text-center text-sm">
            {flowType
              ? flowType === OnboardingFlowType.GetToKnowYou
                ? "Let's Get To Know You!"
                : "Let's Get Started Tackling Your Goals!"
              : 'Coach Logic entering the chat...'}
          </div>
          <div className="flex-1" />
        </div>
        <div className="w-full bg-app-grey-5 font-normal font-inter text-center text-sm text-grey-lighter6x">
          {`Today, ${moment().format('hh:mm A')}`}
        </div>
      </div>
      <div
        className="flex flex-col w-full p-2 sm:p-4 pt-0 overflow-y-scroll h-[600px] gap-8"
        ref={chatContainerRef}
        onScroll={handleUserScroll}
      >
        {isFetchingNextPage && (
          <div className="flex justify-center p-1">
            <LoadingIndicator size={20} />
          </div>
        )}
        {messages.map((message, index) => {
          const isLastMessage = index === messages.length - 1
          const lastUserMessageIndex = findLastIndex(messages, (msg) => msg.sender === 'user')
          const isEditingLastMessage = index === lastUserMessageIndex
          const nextUserMessage = find(messages, (msg) => msg.sender === 'user', index + 1)
          const isEdited = nextUserMessage?.isEdited || false
          if (message.text) {
            return (
              <div
                key={index}
                className={`flex gap-2 ${
                  message.sender === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                <div className={`${message.sender === 'user' ? 'order-2' : ''}`}>
                  <AppAvatar
                    size={'md'}
                    type={!logo && message.sender === 'user' ? 'text' : 'image'}
                    shape={'Rounded'}
                    text={userInitial}
                    color="Primary"
                    src={message.sender === 'user' ? logo : Avatar}
                  />
                </div>
                {/* Voice message display for user voice messages */}
                {message.sender === 'user' && message.isVoiceMessage ? (
                  <AppVoiceIndicator
                    transcript={message.text || ''}
                    duration={message.voiceDuration}
                    className="max-w-[90%]"
                  />
                ) : (
                  /* Regular message bubble */
                  <div
                    className={`w-fit max-w-[90%] flex rounded-lg px-3 pt-2 pb-1.5 text-base text-black-appDark !font-inter relative ${
                      message.sender === 'assistant'
                        ? 'bg-background-appLight text-black'
                        : 'bg-primary-appBrand text-white'
                    }`}
                  >
                    {message.sender === 'assistant' && message.text && isLastMessage ? (
                      <TypingMessage text={message.text} onNewCharacter={handleTypingMessage} />
                    ) : message.sender === 'assistant' ? (
                      <>
                        <SafeMarkdown className="chatbot-markdown">{message.text}</SafeMarkdown>
                        {/* TTS Controls for bot messages */}
                        {ttsSupported && message.text && !isLastMessage && (
                          <div className="flex justify-end mt-2">
                            <AppTextToSpeech
                              text={extractTextForTTS(message.text)}
                              disabled={false}
                              className="opacity-70 hover:opacity-100"
                              onStart={() => {}}
                              onEnd={() => {}}
                              onError={() => {}}
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {message.text}
                        {isEditingLastMessage && !processing && !disableButton && (
                          <button
                            onClick={handleEditLastMessage}
                            className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-lg hover:bg-gray-100 transition-colors"
                            title="Edit message"
                          >
                            <EditIcon className="w-4 h-4 fill-gray-600" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
                {isEdited && <div className="text-xs text-gray-400 mt-1 italic">(edited)</div>}
              </div>
            )
          }
          if (message.content) {
            return (
              <div
                key={index}
                className={`flex gap-2 ${
                  message.sender === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                <div className={`${message.sender === 'user' ? 'order-2' : ''}`}>
                  <AppAvatar
                    size={'md'}
                    type={!logo && message.sender === 'user' ? 'text' : 'image'}
                    shape={'Rounded'}
                    text={userInitial}
                    color="Primary"
                    src={message.sender === 'user' ? logo : Avatar}
                  />
                </div>
                <div className="flex-1">
                  <AppDynamicChatMessage
                    key={index}
                    content={{ ...message.content }}
                    onSubmit={submitDataConfirmationForm}
                    typingAnimationCallback={handleTypingMessage}
                    useTypingAnimation={index === messages.length - 1}
                    isLastMessage={isLastMessage}
                    isSendingMessageTobot={isSendingMessageTobot}
                    tools={tools}
                    setIsUpload={setIsUpload}
                    showUploadModal={showUploadModal}
                    isUpload={isUpload}
                    uploadedFile={uploadedFile}
                    setUploadedFile={setUploadedFile}
                    startUpload={startUpload}
                    setStartUpload={setStartUpload}
                    scrollToBottom={scrollToBottom}
                    refetchBusinessGoal={refetchBusinessGoal}
                    refetchTool={refetch}
                    addMessage={addMessage}
                  />
                </div>
                {message.sender === 'user' && message.isEdited && (
                  <div className="text-xs text-gray-400 mt-1 italic">(edited)</div>
                )}
              </div>
            )
          }
          return <></>
        })}
        <div className="text-center w-full bg-app-grey-5 font-normal font-inter text-xs text-primary-appBrand flex justify-center cursor-pointer">
          {!(processing || disableButton || !messages.length) && (
            <div className="chatbot-help py-1 px-2 hover:bg-blue" onClick={helpDesk}>
              Get Help?
            </div>
          )}
        </div>
        {processing || isSendingMessageTobot ? <AppTypingIndicator /> : <></>}
      </div>
      <div className="p-4 sm:p-8 pt-0">
        {isEditingLastMessage && !processing && !isSendingMessageTobot && (
          <div className="mb-4">
            <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
              Editing your last response
            </div>

            {editingStepType === OnboardingFlowStepTypes.Question ? (
              // Render text input for editing questions
              <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
                <textarea
                  rows={4}
                  value={editingMessageText}
                  onChange={(e) => setEditingMessageText(e.target.value)}
                  placeholder="Edit your response..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
                      e.preventDefault()
                      handleSaveEdit()
                    } else if (e.key === 'Escape') {
                      e.preventDefault()
                      handleCancelEdit()
                    }
                  }}
                  className="w-full resize-none font-inter text-base text-black-appDark leading-5 bg-white border border-gray-300 rounded-md p-3 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
                />
                <div className="flex gap-2 mt-3">
                  <AppButton
                    variant="secondary"
                    size="sm"
                    label="Cancel"
                    onClick={handleCancelEdit}
                  />
                  <AppButton
                    variant="primary"
                    size="sm"
                    label="Save"
                    disabled={editingMessageText?.trim() === ''}
                    onClick={handleSaveEdit}
                  />
                </div>
              </div>
            ) : (editingStepType === OnboardingFlowStepTypes.Form ||
                editingStepType === OnboardingFlowStepTypes.GoalTemplate) &&
              editingStepContent ? (
              // Render the form for editing
              <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
                <AppDynamicChatMessage
                  content={editingStepContent}
                  onSubmit={(formData) => {
                    handleEditFormSubmit(formData)
                  }}
                  addMessage={addMessage}
                  typingAnimationCallback={() => {}}
                  useTypingAnimation={false}
                  isLastMessage={true}
                  isSendingMessageTobot={false}
                  tools={tools}
                  setIsUpload={setIsUpload}
                  showUploadModal={showUploadModal}
                  isUpload={isUpload}
                  uploadedFile={uploadedFile}
                  setUploadedFile={setUploadedFile}
                  startUpload={startUpload}
                  setStartUpload={setStartUpload}
                  scrollToBottom={scrollToBottom}
                  refetchBusinessGoal={refetchBusinessGoal}
                  refetchTool={refetch}
                />
                <div className="flex gap-2 mt-3">
                  <AppButton
                    variant="secondary"
                    size="sm"
                    label="Cancel"
                    onClick={handleCancelEdit}
                  />
                </div>
              </div>
            ) : null}
          </div>
        )}
        {!isEditingLastMessage && (
          <div className=" flex flex-col rounded-lg px-4 py-3 border border-primary-appDarkBorder">
            <textarea
              disabled={processing || disableButton || isFormStep || isToolConnectionStep}
              rows={6}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Message Coach Logic..."
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
                  e.preventDefault()
                  handleSendMessage()
                } else if ((e.key === 'Enter' && e.shiftKey) || (e.key === 'Enter' && e.ctrlKey)) {
                } else if (e.key === 'Escape') {
                  e.preventDefault()
                  setInputValue('')
                } else if (e.key === 'Backspace' && e.ctrlKey) {
                  e.preventDefault()
                  setInputValue((prev) => {
                    // Delete the last word
                    const lastSpaceIndex = prev?.trimEnd().lastIndexOf(' ') ?? -1
                    return lastSpaceIndex !== -1 ? prev?.substring(0, lastSpaceIndex + 1) ?? '' : ''
                  })
                }
              }}
              className="w-full resize-none font-inter text-base text-black-appDark leading-6 bg-transparent border-none focus-visible:ring-0 focus-visible:outline-none min-h-[120px]"
            />
            <div className="flex justify-between relative w-full">
              <div className="flex gap-2 items-center">
                <AppButton
                  variant="clear"
                  size="sm"
                  RightIcon={AppPaperClipIcon}
                  disabled={
                    !isUpload ||
                    processing ||
                    isSendingMessageTobot ||
                    startUpload ||
                    isToolConnectionStep
                  }
                  onClick={() => setShowUploadModal(!showUploadModal)}
                />
                {/* Audio Recorder */}
                {audioSupported && (
                  <AppAudioRecorder
                    onRecordingComplete={handleAudioRecording}
                    disabled={processing || disableButton || isToolConnectionStep}
                    maxDuration={audioMaxDuration}
                    className="ml-1"
                  />
                )}
                {/* Play button - show for both "Let's Get To Know You!" and "Let's Get Started Tackling Your Goals!" flows */}
                <AppToolTip name="create-play-tooltip" text="Create Play" variant="clear">
                  <AppButton
                    variant="clear"
                    size="sm"
                    RightIcon={AppPlayIcon}
                    disabled={processing || isSendingMessageTobot || isPlayCreated}
                    onClick={() => setShowCreatePlayModal(true)}
                  />
                </AppToolTip>
                {/* <AppVerticalDivider />
                <AppButton
                  onClick={() => setEmojiClicked(!emojiClicked)}
                  variant="transparent"
                  size="sm"
                  RightIcon={AppEmojiIcon}
                />
                <AppButton variant="transparent" size="sm" RightIcon={AppAtSymbol} /> */}
              </div>

              {/* {emojiClicked && !processing && !disableButton && (
                <EmojiPicker
                  style={{
                    position: 'absolute',
                    bottom: '40%',
                    zIndex: 99999,
                    height: '450px',
                    width: '350px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                  }}
                  reactionsDefaultOpen={false}
                  onReactionClick={(e) => setInputValue((prve) => (prve ? prve : '') + e.emoji)}
                  onEmojiClick={(e) => setInputValue((prve) => (prve ? prve : '') + e.emoji)}
                />
              )} */}

              {processing ? (
                <CircleNotchIcon className={'fill-primary h-[30px]'} />
              ) : (
                <AppButton
                  variant="primary"
                  size="sm"
                  RightIcon={AppSendMessageIcon}
                  disabled={processing || disableButton || inputValue?.trim() === ''}
                  onClick={() => handleSendMessage()}
                />
              )}
            </div>
          </div>
        )}
      </div>
      {showUploadModal && (
        <AppModalBox title={'Upload Files'} onClose={() => setShowUploadModal(false)}>
          <div className="m-10">
            <AppFileUploadField
              name="imageData"
              className="flex-1"
              labelFontSize="base"
              maxFiles={10} // Allow up to 5 files
              onChange={(files) => {
                setUploadedFile(files)
              }}
            />
            <div className="mt-20 w-[30%]">
              <AppButton
                variant="primary"
                size="sm"
                label="Upload"
                disabled={!uploadedFile || uploadedFile.length === 0}
                onClick={() => {
                  setStartUpload(true)
                  setShowUploadModal(false)
                }}
              />
            </div>
          </div>
        </AppModalBox>
      )}

      {/* Create Play Modal */}
      {showCreatePlayModal && (
        <AppModalBox
          Icon={AppCrossIcon}
          height="max-h-[95vh] min-h-[50vh] h-fit"
          width="max-w-2xl"
          titleSize="sm"
          title="Create new play"
          showBorder={false}
          onClose={() => setShowCreatePlayModal(false)}
          onBack={() => setShowCreatePlayModal(false)}
        >
          <AppTask
            tenantId={getTenantId()}
            userId={user?.id || ''}
            source={UserTaskSource.General}
            setShowTaskModal={setShowCreatePlayModal}
            setShowTaskSuccessModal={setShowPlaySuccessModal}
            setShowTaskErrorModal={setShowPlayErrorModal}
            setCreatedUserTask={handleSetCreatedUserTask}
            refetch={() => {}} // No specific refetch needed for chat context
            logo={logo || ''}
            userName={`${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || ''}
            advisorsData={advisorsData || []}
            userAvatar={user?.avatar || ''}
            userFirstName={user?.firstName || ''}
            userLastName={user?.lastName || ''}
            goalEndDate=""
            assigneeOptions={assigneeOptions}
            paramUserId={user?.id || ''}
            initialDescription="" // Empty description - no chat context
          />
        </AppModalBox>
      )}

      {/* Play Creation Success/Error Modal */}
      {(showPlaySuccessModal || showPlayErrorModal) && createdUserTask && (
        <AppTaskCreationConfirmation
          showAddTaskSuccessModal={showPlaySuccessModal}
          setShowAddTaskModal={setShowCreatePlayModal}
          setShowAddTaskSuccessModal={setShowPlaySuccessModal}
          setShowAddTaskErrorModal={setShowPlayErrorModal}
          createdUserTask={createdUserTask}
          userId={user?.id || ''}
          userAvatar={user?.avatar || ''}
          userFirstName={user?.firstName || ''}
          userLastName={user?.lastName || ''}
          assigneeOptions={assigneeOptions}
        />
      )}
    </div>
  )
}
